import { useMemo, useState, useCallback } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Select,
  Button,
  Skeleton,
  SimpleGrid,
  Drawer,
  Divider,
  ActionIcon,
  Tooltip,
  Pagination,
  Modal,
  Textarea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconAlertCircle,
  IconCircleCheck,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconEye,
  IconCheck,
  IconX,
  IconHandStop,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import {
  useExceptions,
  useExceptionSummary,
  useAcknowledgeException,
  useResolveException,
  useDismissException,
} from '@/hooks/usePortalApi';
import type { ExceptionItem } from '@/types';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

type SortField = 'created_at' | 'severity' | 'status' | 'exception_type';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 20;

const SEVERITY_ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

export default function ExceptionQueue() {
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Drawer state
  const [drawerItem, setDrawerItem] = useState<ExceptionItem | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // Notes modal state
  const [notesOpened, { open: openNotes, close: closeNotes }] = useDisclosure(false);
  const [notesAction, setNotesAction] = useState<'resolve' | 'dismiss'>('resolve');
  const [notesItemId, setNotesItemId] = useState<number | null>(null);
  const [notesText, setNotesText] = useState('');

  // Queries
  const { data, isLoading } = useExceptions({
    status: statusFilter || undefined,
    severity: severityFilter || undefined,
  });
  const { data: summary } = useExceptionSummary();

  // Mutations
  const acknowledgeMutation = useAcknowledgeException();
  const resolveMutation = useResolveException();
  const dismissMutation = useDismissException();

  const allItems = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allItems;
    return [...allItems].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'created_at') {
        cmp = dayjs(a.created_at).unix() - dayjs(b.created_at).unix();
      } else if (sortField === 'severity') {
        cmp = (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9);
      } else {
        cmp = (a[sortField] ?? '').localeCompare(b[sortField] ?? '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allItems, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const items = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleViewDrawer = useCallback(
    (item: ExceptionItem) => {
      setDrawerItem(item);
      openDrawer();
    },
    [openDrawer],
  );

  const handleAcknowledge = useCallback(
    (item: ExceptionItem) => {
      acknowledgeMutation.mutate(item.id, {
        onSuccess: () => {
          notifications.show({
            title: 'Exception Acknowledged',
            message: `Exception #${item.id} has been acknowledged.`,
            color: 'blue',
          });
          closeDrawer();
        },
        onError: () => {
          notifications.show({
            title: 'Action Failed',
            message: 'Failed to acknowledge exception.',
            color: 'red',
          });
        },
      });
    },
    [acknowledgeMutation, closeDrawer],
  );

  const handleOpenNotes = useCallback(
    (itemId: number, action: 'resolve' | 'dismiss') => {
      setNotesItemId(itemId);
      setNotesAction(action);
      setNotesText('');
      openNotes();
    },
    [openNotes],
  );

  const handleConfirmNotes = useCallback(() => {
    if (!notesItemId) return;
    const mutation = notesAction === 'resolve' ? resolveMutation : dismissMutation;
    mutation.mutate(
      { id: notesItemId, notes: notesText },
      {
        onSuccess: () => {
          notifications.show({
            title: notesAction === 'resolve' ? 'Exception Resolved' : 'Exception Dismissed',
            message: `Exception #${notesItemId} has been ${notesAction === 'resolve' ? 'resolved' : 'dismissed'}.`,
            color: notesAction === 'resolve' ? 'green' : 'gray',
          });
          closeNotes();
          closeDrawer();
        },
        onError: () => {
          notifications.show({
            title: 'Action Failed',
            message: `Failed to ${notesAction} exception.`,
            color: 'red',
          });
        },
      },
    );
  }, [notesItemId, notesAction, notesText, resolveMutation, dismissMutation, closeNotes, closeDrawer]);

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(field)}
    >
      <Group gap={4} wrap="nowrap">
        {children}
        <SortIcon field={field} current={sortField} dir={sortDir} />
      </Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Exception Queue"
        description="View and manage items requiring your attention"
      />

      {/* ---- Summary Stats ---- */}
      <SimpleGrid cols={{ base: 1, sm: 4 }}>
        <StatCard
          title="Total Open"
          value={String(summary?.total_open ?? 0)}
          icon={<IconAlertTriangle size={24} />}
        />
        <StatCard
          title="Critical"
          value={String(summary?.by_severity.critical ?? 0)}
          icon={<IconAlertCircle size={24} />}
        />
        <StatCard
          title="Warning"
          value={String(summary?.by_severity.warning ?? 0)}
          icon={<IconAlertTriangle size={24} />}
        />
        <StatCard
          title="Resolved"
          value={String(summary?.by_status.resolved ?? 0)}
          icon={<IconCircleCheck size={24} />}
        />
      </SimpleGrid>

      {/* ---- Filters ---- */}
      <Group justify="flex-end" gap="xs">
        <Select
          placeholder="Filter by status"
          data={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v ?? ''); setPage(1); }}
          w={170}
          size="sm"
          clearable
        />
        <Select
          placeholder="Filter by severity"
          data={SEVERITY_OPTIONS}
          value={severityFilter}
          onChange={(v) => { setSeverityFilter(v ?? ''); setPage(1); }}
          w={170}
          size="sm"
          clearable
        />
      </Group>

      {/* ---- Table ---- */}
      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconAlertTriangle size={30} />}
          title="No exceptions found"
          description={
            statusFilter || severityFilter
              ? 'Try a different filter'
              : 'No exceptions requiring attention right now'
          }
        />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={70}>ID</Table.Th>
                  <ThSort field="exception_type">Type</ThSort>
                  <Table.Th>Title</Table.Th>
                  <ThSort field="severity">Severity</ThSort>
                  <ThSort field="status">Status</ThSort>
                  <ThSort field="created_at">Created</ThSort>
                  <Table.Th w={100} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                        {item.id}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{item.exception_type.replace(/_/g, ' ')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {item.title}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={item.severity} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={item.status} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {item.created_at ? dayjs(item.created_at).format('MMM D, YYYY') : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="View details">
                          <ActionIcon variant="subtle" size="sm" onClick={() => handleViewDrawer(item)}>
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {item.status === 'open' && (
                          <Tooltip label="Acknowledge">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="blue"
                              onClick={() => handleAcknowledge(item)}
                              loading={acknowledgeMutation.isPending}
                            >
                              <IconHandStop size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination total={totalPages} value={page} onChange={setPage} />
            </Group>
          )}
        </>
      )}

      {/* ---- Quick-view Drawer ---- */}
      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        position="right"
        size="lg"
        title={
          <Text fw={700} size="lg">
            Exception #{drawerItem?.id ?? ''}
          </Text>
        }
      >
        {drawerItem ? (
          <Stack gap="md">
            <Group justify="space-between">
              <StatusBadge status={drawerItem.status} />
              <StatusBadge status={drawerItem.severity} />
            </Group>

            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Type</Text>
                <Text size="sm" fw={500}>{drawerItem.exception_type.replace(/_/g, ' ')}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Created</Text>
                <Text size="sm" fw={500}>
                  {drawerItem.created_at ? dayjs(drawerItem.created_at).format('MMM D, YYYY h:mm A') : '-'}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Source</Text>
                <Text size="sm" fw={500}>
                  {drawerItem.source_type ? `${drawerItem.source_type} #${drawerItem.source_id}` : 'N/A'}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Assigned To</Text>
                <Text size="sm" fw={500}>{drawerItem.assigned_to || 'Unassigned'}</Text>
              </div>
              {drawerItem.resolved_by && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Resolved By</Text>
                  <Text size="sm" fw={500}>{drawerItem.resolved_by}</Text>
                </div>
              )}
              {drawerItem.resolved_at && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Resolved At</Text>
                  <Text size="sm" fw={500}>{dayjs(drawerItem.resolved_at).format('MMM D, YYYY h:mm A')}</Text>
                </div>
              )}
            </SimpleGrid>

            <Divider />

            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>Title</Text>
              <Text size="sm" fw={500}>{drawerItem.title}</Text>
            </div>

            {drawerItem.description && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>Description</Text>
                <Text size="sm">{drawerItem.description}</Text>
              </div>
            )}

            {drawerItem.resolution_notes && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>Resolution Notes</Text>
                <Text size="sm">{drawerItem.resolution_notes}</Text>
              </div>
            )}

            {/* Action buttons */}
            {drawerItem.status !== 'resolved' && drawerItem.status !== 'dismissed' && (
              <>
                <Divider />
                <Group>
                  {drawerItem.status === 'open' && (
                    <Button
                      variant="light"
                      color="blue"
                      leftSection={<IconHandStop size={16} />}
                      onClick={() => handleAcknowledge(drawerItem)}
                      loading={acknowledgeMutation.isPending}
                    >
                      Acknowledge
                    </Button>
                  )}
                  <Button
                    variant="light"
                    color="gray"
                    leftSection={<IconX size={16} />}
                    onClick={() => handleOpenNotes(drawerItem.id, 'dismiss')}
                  >
                    Dismiss
                  </Button>
                  <Button
                    leftSection={<IconCheck size={16} />}
                    onClick={() => handleOpenNotes(drawerItem.id, 'resolve')}
                  >
                    Resolve
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        ) : (
          <Stack gap="xs">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} height={30} radius="md" />
            ))}
          </Stack>
        )}
      </Drawer>

      {/* ---- Resolve/Dismiss Notes Modal ---- */}
      <Modal
        opened={notesOpened}
        onClose={closeNotes}
        title={
          <Text fw={600}>
            {notesAction === 'resolve' ? 'Resolve Exception' : 'Dismiss Exception'}
          </Text>
        }
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {notesAction === 'resolve'
              ? 'Provide resolution notes for this exception.'
              : 'Provide a reason for dismissing this exception.'}
          </Text>
          <Textarea
            label="Notes"
            placeholder={
              notesAction === 'resolve'
                ? 'Describe how this was resolved...'
                : 'Reason for dismissal...'
            }
            value={notesText}
            onChange={(e) => setNotesText(e.currentTarget.value)}
            minRows={3}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeNotes}>
              Cancel
            </Button>
            <Button
              color={notesAction === 'resolve' ? 'brand' : 'gray'}
              onClick={handleConfirmNotes}
              loading={resolveMutation.isPending || dismissMutation.isPending}
            >
              {notesAction === 'resolve' ? 'Resolve' : 'Dismiss'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
